import React, { useState, useEffect, useCallback } from 'react';
import { Webhook, Plus, Pencil, Trash2, Loader2, Play, X, Check, AlertCircle } from 'lucide-react';
import { authAPI, isAnyAdmin } from '../lib/auth';

const EMPTY_FORM = {
  name: '', push_type: 'webhook', endpoint_url: '', auth_type: 'none',
  auth_header_value: '', custom_headers: '{}', events: ['scan_completed'],
  delivery_format: 'json', is_active: true, retry_count: 3,
};

const EVENT_OPTIONS = [
  'scan_completed', 'classification_done', 'risk_flagged', 'report_generated',
  'workflow_completed', 'merchant_deboarded', 'bulk_completed',
];

export default function DataPushPage() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);
  const admin = isAnyAdmin();

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAPI.listPushConfigs();
      setConfigs(res.data.configs || []);
    } catch {
      setError('Failed to load push configs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError(null);
    setModalOpen(true);
  };

  const openEdit = (config) => {
    setEditing(config);
    setForm({
      name: config.name || '',
      push_type: config.push_type || 'webhook',
      endpoint_url: config.endpoint_url || '',
      auth_type: config.auth_type || 'none',
      auth_header_value: config.auth_header_value || '',
      custom_headers: typeof config.custom_headers === 'string' ? config.custom_headers : JSON.stringify(config.custom_headers || {}),
      events: Array.isArray(config.events) ? config.events : JSON.parse(config.events || '[]'),
      delivery_format: config.delivery_format || 'json',
      is_active: config.is_active !== false,
      retry_count: config.retry_count || 3,
    });
    setError(null);
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      let parsedHeaders = {};
      try { parsedHeaders = JSON.parse(form.custom_headers); } catch { /* ignore */ }

      const payload = { ...form, custom_headers: parsedHeaders };

      if (editing) {
        await authAPI.updatePushConfig(editing.id, payload);
      } else {
        await authAPI.createPushConfig(payload);
      }
      setModalOpen(false);
      loadConfigs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this push configuration?')) return;
    try {
      await authAPI.deletePushConfig(id);
      loadConfigs();
    } catch {
      setError('Failed to delete config');
    }
  };

  const handleTest = async (id) => {
    setTesting(id);
    setTestResult(null);
    try {
      const res = await authAPI.testPushConfig(id);
      setTestResult({ id, ...res.data });
    } catch (err) {
      setTestResult({ id, status: 'failed', error: err.response?.data?.detail || 'Test failed' });
    } finally {
      setTesting(null);
    }
  };

  const toggleEvent = (event) => {
    setForm(prev => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter(e => e !== event)
        : [...prev.events, event],
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Webhook className="w-6 h-6 text-teal-600" />
          <h1 className="text-2xl font-bold text-gray-900">Data Push Config</h1>
        </div>
        {admin && (
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">
            <Plus className="w-4 h-4" /> New Config
          </button>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      ) : (
        <div className="grid gap-4">
          {configs.map(c => (
            <div key={c.id} className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold text-gray-900">{c.name}</h3>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    c.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {c.is_active ? 'Active' : 'Disabled'}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-teal-700">
                    {c.push_type}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleTest(c.id)}
                    disabled={testing === c.id}
                    className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {testing === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                    Test
                  </button>
                  {admin && (
                    <>
                      <button onClick={() => openEdit(c)} className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(c.id)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="text-sm text-gray-500 truncate">{c.endpoint_url || 'No endpoint set'}</div>
              <div className="flex gap-2 flex-wrap">
                {(Array.isArray(c.events) ? c.events : JSON.parse(c.events || '[]')).map(ev => (
                  <span key={ev} className="px-2 py-0.5 rounded text-[10px] bg-teal-50 text-teal-700">{ev}</span>
                ))}
              </div>
              {/* Test result */}
              {testResult && testResult.id === c.id && (
                <div className={`mt-2 p-3 rounded-lg text-sm ${
                  testResult.status === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`}>
                  <div className="flex items-center gap-2">
                    {testResult.status === 'success' ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    <span className="font-medium">{testResult.status === 'success' ? 'Delivered' : 'Failed'}</span>
                    {testResult.status_code && <span className="text-xs opacity-70">HTTP {testResult.status_code}</span>}
                    {testResult.duration_ms && <span className="text-xs opacity-70">{testResult.duration_ms}ms</span>}
                  </div>
                  {testResult.error && <div className="mt-1 text-xs">{testResult.error}</div>}
                </div>
              )}
            </div>
          ))}
          {configs.length === 0 && (
            <div className="text-center py-10 text-gray-400">No push configurations</div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">{editing ? 'Edit Config' : 'New Push Config'}</h2>
              <button onClick={() => setModalOpen(false)} className="p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={form.push_type} onChange={e => setForm(p => ({ ...p, push_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                    <option value="webhook">Webhook</option>
                    <option value="s3">S3</option>
                    <option value="email">Email</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
                  <select value={form.delivery_format} onChange={e => setForm(p => ({ ...p, delivery_format: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                    <option value="json">JSON</option>
                    <option value="csv">CSV</option>
                    <option value="xml">XML</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint URL</label>
                <input value={form.endpoint_url} onChange={e => setForm(p => ({ ...p, endpoint_url: e.target.value }))}
                  placeholder="https://your-webhook.example.com/receive"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Auth Type</label>
                  <select value={form.auth_type} onChange={e => setForm(p => ({ ...p, auth_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                    <option value="none">None</option>
                    <option value="bearer">Bearer Token</option>
                    <option value="api_key">API Key</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Auth Value</label>
                  <input type="password" value={form.auth_header_value}
                    onChange={e => setForm(p => ({ ...p, auth_header_value: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Events</label>
                <div className="flex flex-wrap gap-2">
                  {EVENT_OPTIONS.map(ev => (
                    <button key={ev} type="button" onClick={() => toggleEvent(ev)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        form.events.includes(ev) ? 'bg-teal-100 border-teal-300 text-teal-800' : 'bg-gray-50 border-gray-200 text-gray-600'
                      }`}>
                      {ev}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Retry Count</label>
                  <input type="number" value={form.retry_count} min={0} max={10}
                    onChange={e => setForm(p => ({ ...p, retry_count: parseInt(e.target.value) || 3 }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.is_active}
                      onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))}
                      className="rounded" />
                    Active
                  </label>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Update' : 'Create')}
                </button>
                <button type="button" onClick={() => setModalOpen(false)}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
